import { faAt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Dialog from '@mui/material/Dialog';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useEffect, useState } from 'react';
import LoginServices from '../Login/LoginServices';
// eslint-disable-next-line import/no-cycle
import EasyLoginButton from './EasyLoginButton';
import KeychainLogin from './KeychainLogin';
import AuthMethodButton from '../Onboarding/AuthMethodButton';
import JournalDivider from '../Onboarding/JournalDivider';
import { figtree } from '../Landing/fonts';
import {
  Accent,
  DisplayHeading,
  GhostButton,
  MUTED,
} from '../Landing/ui';

// Re-boarding (login) dialog in the expedition-journal skin. The MUI Dialog
// stays as the invisible behavioral shell — all visible styling is Tailwind.
const LoginDialog = props => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [showReset, setShowReset] = useState(false);
  const [hiveKeychain, setHiveKeychain] = useState(undefined);
  const [showEmail, setShowEmail] = useState(false);

  useEffect(() => {
    if (hiveKeychain === undefined && window && window.hive_keychain) {
      setHiveKeychain(true);
    }
  });

  const { open, handleClose } = props;

  const textLink = (label, onClick) => (
    <div className="tw-pb-2 tw-text-center">
      <button
        type="button"
        onClick={onClick}
        className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-[15px] tw-font-semibold tw-text-primary-700 dark:tw-text-primary-300 hover:tw-underline"
      >
        {label}
      </button>
    </div>
  );

  return (
    <>
      <Dialog
        maxWidth={hiveKeychain ? 'xs' : false}
        PaperProps={{
          className: `tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] ${
            fullScreen ? '' : 'tw-rounded-3xl'
          }`,
          style: hiveKeychain ? {} : { maxWidth: '880px', width: '100%' },
        }}
        fullScreen={fullScreen}
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <div
          className={`${figtree.className} signup-flow tw-relative ${
            fullScreen ? 'tw-flex-1 tw-overflow-y-auto tw-pb-48' : ''
          }`}
        >
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="tw-absolute tw-right-3 tw-top-3 tw-z-10 tw-flex tw-h-10 tw-w-10 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-[#5F6F69] dark:tw-text-[#93A69E] hover:tw-bg-[#1D2D28]/5 dark:hover:tw-bg-white/10"
          >
            <svg
              className="tw-h-5 tw-w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>

          {hiveKeychain ? (
            <div className="tw-px-6 tw-pb-6 tw-pt-10 sm:tw-px-8">
              <div className="tw-mb-6 tw-text-center">
                <p className="tw-mb-3 tw-mt-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-primary-700 dark:tw-text-primary-300">
                  Re-boarding · welcome back
                </p>
                <DisplayHeading
                  as="h2"
                  className="tw-text-2xl tw-leading-tight"
                  id="form-dialog-title"
                >
                  Log in with Hive <Accent>Keychain</Accent>.
                </DisplayHeading>
                <p className={`${MUTED} tw-mb-0 tw-mt-3 tw-text-sm`}>
                  To log in with Hive Keychain, please enter your Hive username
                  and click the Keychain button
                </p>
              </div>
              <KeychainLogin journal setHiveKeychain={setHiveKeychain} />
              <JournalDivider label="Or log in with" className="tw-my-5" />
              <div className="tw-text-center">
                <GhostButton onClick={() => setHiveKeychain(false)}>
                  Use a different login method
                </GhostButton>
              </div>
            </div>
          ) : (
            <div className="tw-px-6 tw-pb-6 tw-pt-10 sm:tw-px-8">
              {/* Header */}
              <div className="tw-mb-7 tw-text-center">
                <p className="tw-mb-3 tw-mt-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-primary-700 dark:tw-text-primary-300">
                  Re-boarding
                </p>
                <DisplayHeading
                  as="h2"
                  className="tw-text-2xl tw-leading-tight sm:tw-text-3xl"
                  id="form-dialog-title"
                >
                  Welcome <Accent>back</Accent>.
                </DisplayHeading>
              </div>

              <div className="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-[1.15fr_auto_1fr] md:tw-gap-8">
                {/* Email login (desktop always; mobile after choosing Email) */}
                {(showEmail || !fullScreen) && (
                  <div>
                    <p
                      className={`${MUTED} tw-mb-3 tw-mt-0 tw-text-sm tw-font-medium`}
                    >
                      Log in with email
                    </p>
                    <EasyLoginButton journal setShowReset={setShowReset} />
                    {showReset && (
                      <>
                        <JournalDivider
                          label="Can't log in?"
                          className="tw-my-4"
                        />
                        {textLink(
                          'Reset your password',
                          props.handleLoginResetSwitch,
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Tear-off perforation */}
                <JournalDivider vertical className="tw-hidden md:tw-block" />

                {/* Service stub */}
                <div>
                  {fullScreen && !showEmail ? (
                    <div className="tw-mb-2">
                      <AuthMethodButton
                        primary
                        isLogin
                        onClick={() => setShowEmail(true)}
                        logo={
                          <FontAwesomeIcon
                            style={{ width: 22, height: 22 }}
                            icon={faAt}
                            className="tw-text-white"
                          />
                        }
                        name="Email"
                      />
                    </div>
                  ) : (
                    <JournalDivider
                      label="Or via"
                      className="tw-mb-3 md:tw-mb-3 md:tw-mt-1"
                    />
                  )}
                  <LoginServices journal isLogin />
                </div>
              </div>

              {/* Footer */}
              <JournalDivider label="No account?" className="tw-my-5" />
              {textLink('Create your account now', props.switch)}
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default LoginDialog;
